import { Box, Stack } from "@mui/joy";
import Button from "@mui/joy/Button";
import { ProgramContentType } from "data/BlockComponent";
import { SingleSelectionType } from "data/CellComponent";
import * as _ from "lodash";
import React, { useEffect, useState } from "react";

interface ButtonGroupOptionV2 {
  text: string;
  showFromId?: string;
  showToId?: string;
  restartFromId?: string;
  hideFromId?: string;
}

type BranchableProgramContent = ProgramContentType & {
  __taskEditorBranchBaseNextBlockId?: string;
};

function normalizeBranchTargetId(value?: string) {
  const normalizedValue = `${value ?? ""}`.trim();
  return normalizedValue.length > 0 ? normalizedValue : undefined;
}

function getBlockIndexById(data: ProgramContentType[], blockId?: string) {
  const normalizedBlockId = normalizeBranchTargetId(blockId);
  if (!normalizedBlockId) {
    return -1;
  }

  return data.findIndex(
    (element) => element.blockId && element.blockId === normalizedBlockId
  );
}

function resetBranchNextBlockIds(
  data: ProgramContentType[],
  sourceBlockIndex: number
) {
  data.forEach((block, index) => {
    if (index <= sourceBlockIndex) {
      return;
    }

    const branchableBlock = block as BranchableProgramContent;
    if (branchableBlock.__taskEditorBranchBaseNextBlockId === undefined) {
      branchableBlock.__taskEditorBranchBaseNextBlockId = block.nextBlockId;
    }

    block.nextBlockId = branchableBlock.__taskEditorBranchBaseNextBlockId;
  });
}

function applyRangeBranchVisibility(
  data: ProgramContentType[],
  blockIndex: number,
  showFromIndex: number,
  showToIndex: number
) {
  const startIndex = Math.min(showFromIndex, showToIndex);
  const endIndex = Math.max(showFromIndex, showToIndex);

  data.forEach((block, index) => {
    if (index <= blockIndex) {
      return;
    }

    const shouldShow = index >= startIndex && index <= endIndex;
    block.isHidden = !shouldShow;
    if (!shouldShow) {
      block.isShown = false;
    }
  });

  return endIndex;
}

function getBranchHideResult(
  index: number,
  blockIndex: number,
  showFromIndex: number,
  hideFromIndex: number,
  data: ProgramContentType[]
) {
  if (index <= blockIndex) {
    return null;
  }

  if (showFromIndex > -1 && hideFromIndex > -1) {
    const hideFromIndex2 = data.findIndex(
      (element, searchIndex) =>
        searchIndex > showFromIndex &&
        searchIndex < hideFromIndex &&
        Boolean(element.nextBlockId)
    );
    const showFromIndex3 = data.findIndex(
      (element, searchIndex) =>
        searchIndex > showFromIndex && Boolean(element.nextBlockId)
    );

    if (showFromIndex < hideFromIndex) {
      return (
        index < showFromIndex ||
        index >= hideFromIndex ||
        (hideFromIndex2 > -1 && index > hideFromIndex2)
      );
    }

    if (showFromIndex > hideFromIndex) {
      return (
        index < showFromIndex ||
        (showFromIndex3 > -1 && index >= showFromIndex3)
      );
    }

    return index < showFromIndex;
  }

  if (showFromIndex > -1) {
    return index < showFromIndex;
  }

  if (hideFromIndex > -1) {
    return index >= hideFromIndex;
  }

  return null;
}

export default function ButtonGroupCellV2({
  defaultValue,
  selectedIndex,
  blockIndex,
  options,
  setData,
  lineIndex,
  cellIndex,
}: {
  defaultValue?: string;
  selectedIndex?: number;
  blockIndex: number;
  options: ButtonGroupOptionV2[];
  setData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  lineIndex: number;
  cellIndex: number;
}) {
  const [selectedOption, setSelectedOption] = useState<{
    text?: string;
    index?: number;
  }>({ text: defaultValue, index: selectedIndex });

  useEffect(() => {
    const index =
      selectedIndex !== undefined
        ? selectedIndex
        : options.findIndex((element) => element.text === defaultValue);

    setSelectedOption({ text: defaultValue, index });
  }, [defaultValue, selectedIndex, options]);

  const updateSelection = (selected?: string, optionIndex?: number) => {
    setData((data) => {
      if (
        !data ||
        !data[blockIndex] ||
        !data[blockIndex].lines?.[lineIndex]?.[cellIndex]
      ) {
        return data;
      }

      const nextData = _.cloneDeep(data);
      const currentContent =
        (nextData[blockIndex].lines[lineIndex][cellIndex].content ||
          {}) as SingleSelectionType;
      const selectedOptionFromContent =
        optionIndex !== undefined &&
        Array.isArray(currentContent.options) &&
        optionIndex >= 0 &&
        optionIndex < currentContent.options.length
          ? currentContent.options[optionIndex]
          : undefined;
      const selectedOption =
        selectedOptionFromContent ??
        (optionIndex !== undefined &&
        optionIndex >= 0 &&
        optionIndex < options.length
          ? options[optionIndex]
          : undefined);
      const showFromIndex = getBlockIndexById(
        nextData,
        selectedOption?.showFromId
      );
      const showToIndex = getBlockIndexById(nextData, selectedOption?.showToId);
      const restartFromId = normalizeBranchTargetId(selectedOption?.restartFromId);
      const hideFromIndex = getBlockIndexById(
        nextData,
        selectedOption?.hideFromId
      );

      resetBranchNextBlockIds(nextData, blockIndex);

      const hasRangeBranch = showFromIndex > -1 && showToIndex > -1;

      if (hasRangeBranch) {
        const endIndex = applyRangeBranchVisibility(
          nextData,
          blockIndex,
          showFromIndex,
          showToIndex
        );
        if (restartFromId && endIndex > -1 && nextData[endIndex]) {
          nextData[endIndex].nextBlockId = restartFromId;
        }
      } else if (showFromIndex > -1 || hideFromIndex > -1) {
        nextData.forEach((_, index) => {
          const shouldHide = getBranchHideResult(
            index,
            blockIndex,
            showFromIndex,
            hideFromIndex,
            nextData
          );

          if (shouldHide !== null) {
            nextData[index].isHidden = shouldHide;
            if (shouldHide) {
              nextData[index].isShown = false;
            }
          }
        });
      }

      nextData[blockIndex].nextBlockId =
        normalizeBranchTargetId(selectedOption?.showFromId) ||
        `buttongroup_option_${selected ?? ""}`;

      nextData[blockIndex].lines[lineIndex][cellIndex].content = {
        ...currentContent,
        value: selected,
        selectedIndex: optionIndex,
      };

      return nextData;
    });
  };

  const handleButtonClick = (option: string, optionIndex: number) => {
    if (selectedOption.text === option) {
      return;
    }

    setSelectedOption({ text: option, index: optionIndex });
    updateSelection(option, optionIndex);
  };

  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      sx={{
        width: "100%",
        whiteSpace: "nowrap",
        wordBreak: "keep-all",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      {options.map((option, optionIndex) => (
        <Box
          key={`buttongroup_option_v2_${blockIndex}_${lineIndex}_${cellIndex}_${optionIndex}`}
          sx={{ flexGrow: 1 }}
        >
          <Button
            fullWidth
            size="md"
            variant="Selection"
            color={selectedOption.text === option.text ? "primary" : "secondary"}
            value={option.text}
            onClick={(e) => {
              handleButtonClick(option.text, optionIndex);
              e.currentTarget.blur();
            }}
          >
            {option.text}
          </Button>
        </Box>
      ))}
    </Stack>
  );
}
